import React from 'react';
import CustomBorderButton from '../../../../../components/buttons/CustomBorderButton';
import SearchableSelect from '../../../../../components/SearchableSelect';
import DateRangePicker from '../../../../../components/DateRangePicker';
import {ExportTypeSelector, ExportTypeInfo} from './ExportTypeComponents';
import {horizonLabel} from '../utils/horizonLabels';
import {estimateExport, formatEstimateHint} from '../utils/exportSizeEstimate';
import {ENSEMBLE_MODELS} from '../../../../../constants/ensembleModels';

/**
 * Export request section component
 */
export const ExportRequestSection = ({
  exportType,
  setExportType,
  exportStartDate,
  setExportStartDate,
  exportEndDate,
  setExportEndDate,
  exportMessage,
  showTargetUserSelect,
  isMarketOwnerOrAdmin,
  selectedResource,
  targetUserId,
  setTargetUserId,
  selectedHorizon,
  setSelectedHorizon,
  availableHorizons,
  availableHorizonsLoading,
  includeEnsemble,
  setIncludeEnsemble,
  ensembleModel,
  setEnsembleModel,
  anonymize,
  setAnonymize,
  paymentFilter,
  setPaymentFilter,
  participants,
  onSubmit,
  onReset
}) => {
  const estimate = estimateExport({
    horizon: selectedHorizon,
    startDate: exportStartDate,
    endDate: exportEndDate,
  });
  const hintText = formatEstimateHint(estimate);
  const hintTone =
    estimate?.mode === 'async-slow' ? 'amber'
    : estimate?.mode === 'async-fast' ? 'blue'
    : 'gray';

  return (
    <div className="p-6">
      <div className="flex items-start gap-2.5 mb-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Request Data Export
        </h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
        Request historical forecast data or market reports
      </p>

      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        Export Type
      </label>

      <ExportTypeSelector exportType={exportType} setExportType={setExportType} />

      <ExportTypeInfo exportType={exportType} selectedHorizon={selectedHorizon} />

      {showTargetUserSelect && selectedResource && (
        <>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Target User {exportType === 'historical_forecast' ? <span className="text-red-500">*</span> : '(Optional)'}
          </label>
          <div className="mb-3">
            <SearchableSelect
              value={targetUserId}
              onChange={(value) => setTargetUserId(value)}
              options={(participants || []).map(p => ({
                value: p.user,
                // Email is superuser-only (audit A260528-API-H2); non-SU callers
                // fall back to the username pseudonym.
                label: p.user_email || p.user_username || p.email || `User ${p.user}`,
                isActive: true
              }))}
              placeholder={exportType === 'historical_forecast' ? 'Select target user *' : 'Select target user (optional)'}
              showCount={false}
              enableActivityFilter={false}
            />
          </div>
        </>
      )}

      {/* Horizon selector — required for forecast/market exports, hidden for raw measurements */}
      {exportType !== 'raw_measurement' && (
        <>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Horizon <span className="text-red-500">*</span>
          </label>
          <div className="mb-3">
            <select
              value={selectedHorizon ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedHorizon(v === '' ? null : Number(v));
              }}
              disabled={!selectedResource || availableHorizonsLoading}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm h-[42px] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
            >
              <option value="">
                {!selectedResource
                  ? 'Select a resource first'
                  : availableHorizonsLoading
                    ? 'Loading horizons...'
                    : 'Select horizon'}
              </option>
              {(availableHorizons || []).map((h) => (
                <option key={h} value={h}>{horizonLabel(h)}</option>
              ))}
            </select>
            {selectedResource && !availableHorizonsLoading && (availableHorizons || []).length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                No horizons available for this resource. Configure a Challenge Template first.
              </p>
            )}
          </div>
        </>
      )}

      {/* Include Ensemble and Anonymize Checkboxes (only for Market Report and Market Owners/Admins) */}
      {exportType === 'market' && isMarketOwnerOrAdmin && (
        <>
          <div className="mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeEnsemble}
                onChange={(e) => setIncludeEnsemble(e.target.checked)}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Include Ensemble</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              Include ensemble model scores in the market report
            </p>
          </div>

          {includeEnsemble && (
            <div className="mb-3 ml-6">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ensemble Strategy
              </label>
              <select
                value={ensembleModel ?? 'weighted_avg'}
                onChange={(e) => setEnsembleModel(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm h-[42px] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {ENSEMBLE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={anonymize}
                onChange={(e) => setAnonymize(e.target.checked)}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Anonymize Users</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              Replace user names/emails with anonymous identifiers in the report
            </p>
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Type
            </label>
            <select
              value={paymentFilter ?? 'all'}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm h-[42px] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">All users</option>
              <option value="non_fixed">Market (non-fixed) only</option>
              <option value="fixed">Fixed payment only</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Restrict the report to fixed-payment or market participants. Ranks are recomputed within the filtered set.
            </p>
          </div>
        </>
      )}

      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        Date Range
      </label>
      <div className="mb-3">
        <DateRangePicker
          startDate={exportStartDate}
          endDate={exportEndDate}
          onDateRangeChange={(newStart, newEnd) => {
            setExportStartDate(newStart);
            setExportEndDate(newEnd);
          }}
        />
      </div>

      {hintText && (
        <p className={`-mt-2 mb-3 text-xs flex items-center gap-1.5 ${
          hintTone === 'amber' ? 'text-amber-700 dark:text-amber-400'
          : hintTone === 'blue' ? 'text-blue-700 dark:text-blue-400'
          : 'text-gray-500 dark:text-gray-400'
        }`}>
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>{hintText}</span>
        </p>
      )}

      <div className="flex gap-2">
        <CustomBorderButton
          onClick={onSubmit}
          captions={{idle: 'Request Export'}}
          size="medium"
        />
        <button
          onClick={onReset}
          className="text-xs text-gray-600"
        >
          Reset
        </button>
      </div>

      {exportMessage && (
        <div className="mt-2 text-xs text-gray-600">
          {exportMessage}
        </div>
      )}
    </div>
  );
};

